// BuildMenu - Building placement menu for TypeTown
import { Home } from 'lucide-react';

const BuildMenu = ({
  island,
  currentLevel,
  selectedBuilding,
  setSelectedBuilding,
  setShowBuildMenu,
  setDraggingBuilding,
  handleDragStart,
  placeBuilding,
  getUnlockedBuildings,
  ALL_BUILDINGS,
  BUILDINGS,
}) => {
  return (
    <div
      className="absolute z-[270] bg-white/95 backdrop-blur-md border-4 border-sunny-yellow shadow-2xl shadow-sunny-yellow/20 overflow-hidden flex flex-col top-0 left-0 right-0 rounded-b-2xl max-h-[45vh] sm:bottom-32 sm:top-auto sm:right-4 sm:left-auto sm:rounded-3xl sm:max-h-[70vh] sm:w-[400px]"
      data-testid="build-menu"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header - always visible */}
      <div className="flex justify-between items-center p-3 sm:p-4 border-b border-soft-gray/30 shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-sunny-yellow to-coral-orange rounded-lg sm:rounded-xl flex items-center justify-center shadow-md">
            <Home className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </div>
          <div>
            <h3 className="text-text-dark font-display font-bold text-sm sm:text-lg leading-tight">Build</h3>
            <p className="text-text-medium text-[10px] sm:text-xs">{island?.credits || 0} coins &middot; Lv{currentLevel}</p>
          </div>
        </div>
        <button
          onClick={() => { setShowBuildMenu(false); setSelectedBuilding(null); setDraggingBuilding(null); }}
          className="w-7 h-7 sm:w-8 sm:h-8 bg-coral-orange/20 hover:bg-coral-orange/40 rounded-full flex items-center justify-center text-coral-orange font-bold transition-all text-sm"
          data-testid="close-build-menu"
        >
          ✕
        </button>
      </div>

      {/* Scrollable content */}
      <div className="overflow-y-auto flex-1 p-3 sm:p-4">
        {selectedBuilding && (
          <div className="mb-2 sm:mb-3 p-2 bg-gradient-to-r from-sunny-yellow/30 to-mint-green/30 border border-sunny-yellow rounded-xl text-text-dark text-xs sm:text-sm text-center font-semibold">
            {ALL_BUILDINGS[selectedBuilding]?.name || BUILDINGS[selectedBuilding]?.name} selected - Tap grass to place!
          </div>
        )}

        {/* Building Grid */}
        <div className="grid grid-cols-4 sm:grid-cols-3 gap-1.5 sm:gap-2">
          {getUnlockedBuildings(island?.level_info?.levels_completed || 0).map((building) => {
            const isSelected = selectedBuilding === building.type;
            const canAfford = (island?.credits || 0) >= building.cost;
            return (
              <div
                key={building.level}
                draggable={canAfford}
                onDragStart={(e) => canAfford && handleDragStart(building.type, e)}
                onClick={(e) => {
                  e.stopPropagation();
                  if (canAfford) {
                    setSelectedBuilding(isSelected ? null : building.type);
                  }
                }}
                className={`p-1.5 sm:p-2 rounded-lg sm:rounded-xl border-2 transition-all cursor-pointer ${
                  isSelected
                    ? 'border-sunny-yellow bg-sunny-yellow/20 ring-2 ring-sunny-yellow/30 shadow-lg'
                    : canAfford
                      ? 'border-soft-gray bg-white hover:border-sunny-yellow hover:bg-sunny-yellow/10 shadow-sm'
                      : 'border-coral-orange/30 bg-coral-orange/10 cursor-not-allowed'
                }`}
              >
                <div className="text-xl sm:text-2xl text-center leading-none">{building.emoji}</div>
                <p className="text-text-dark text-[9px] sm:text-xs text-center font-semibold truncate mt-0.5">{building.name}</p>
                <p className={`text-[9px] sm:text-xs text-center font-semibold ${canAfford ? 'text-mint-green' : 'text-coral-orange'}`}>
                  {building.cost}
                </p>
              </div>
            );
          })}
        </div>

        {getUnlockedBuildings(island?.level_info?.levels_completed || 0).length === 0 && (
          <div className="text-center py-4 text-text-medium">
            <p className="text-sm">Complete Level 1 to unlock buildings!</p>
          </div>
        )}
      </div>

      {/* Footer actions - always visible */}
      {selectedBuilding && (
        <div className="shrink-0 p-3 sm:p-4 border-t border-soft-gray/30 flex gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); setSelectedBuilding(null); }}
            className="px-3 py-2 bg-coral-orange/20 border border-coral-orange rounded-xl text-coral-orange text-xs sm:text-sm font-semibold transition-all"
          >
            Cancel
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); placeBuilding(); }}
            className="flex-1 py-2 bg-gradient-to-r from-mint-green to-sky-blue hover:from-sky-blue hover:to-mint-green rounded-xl text-white text-xs sm:text-sm font-bold transition-all shadow-md"
            data-testid="place-building-btn"
          >
            Place Here
          </button>
        </div>
      )}
    </div>
  );
};

export default BuildMenu;
